import { useMemo, useState } from 'react';

import { useField, useFormState } from 'react-final-form';

import { ListSlackChannelsResponse } from '@cast/types';
import { CAST_AI } from '@cast/constants';
import {
  Button,
  Divider,
  IconButton,
  Icons,
  Input,
  Stack,
  Typography,
  tokens,
} from '@cast/design-system';
import { useRffFormState } from '@cast/shared-rff-ui';

import { CopyToClipboard } from 'components/clickable-actions';
import { InlineCode } from 'components/InlineCode';
import { useGetSlackChannels } from 'hooks/queries/notifications';
import { FormModel } from './types';

type SlackChannel = NonNullable<ListSlackChannelsResponse['channels']>[number];

const MAX_CHANNELS = 5;

const CHANNEL_ID_REGEX = /^[CG][A-Z0-9]{8,11}$/;

function CopyCommand() {
  return (
    <Stack
      component="span"
      direction="row"
      alignItems="center"
      gap="2px"
      sx={{ display: 'inline-flex', verticalAlign: 'middle', mx: '2px' }}
    >
      <InlineCode>{`/invite @${CAST_AI}`}</InlineCode>
      <CopyToClipboard
        text={`/invite @${CAST_AI}`}
        iconSize={12}
        color={tokens.theme.fg.structural.secondary}
        tooltip="Copy to clipboard"
      />
    </Stack>
  );
}

function ChannelIdHint() {
  return (
    <>
      <Divider sx={{ mt: 16, mb: 8 }} />
      <Stack direction="column" gap="6px" alignItems="flex-start" pt="6px">
        <Typography variant="P14B">Where to find the channel ID</Typography>
        <Typography
          variant="P12R"
          color="textSecondary"
          lineHeight="20px"
          component="p"
          margin={0}
        >
          In Slack, open the channel, click its name, and copy the Channel ID at
          the bottom of the About tab. For private channels, use <CopyCommand />
          in the channel so the app can post. Always use the Test button to
          confirm the channel exists and is accessible, as the Add action only
          verifies the ID format. Channels and private channel names are never
          listed here.
        </Typography>
      </Stack>
    </>
  );
}

function PrivateChannelNoteInline() {
  return (
    <Stack direction="row" gap="6px" alignItems="flex-start" pt="12px">
      <Typography
        variant="P12R"
        color="textSecondary"
        lineHeight="20px"
        component="p"
        margin={0}
      >
        Private channel names are not shown in {CAST_AI}. Alerts still post to
        the channel.
      </Typography>
    </Stack>
  );
}

// Kept for backwards compatibility — now contains channel ID guidance
function PrivateChannelNote() {
  return <ChannelIdHint />;
}

export const SlackChannelSelect = () => {
  const { input: channelsField, meta: channelsMeta } =
    useField<string[]>('slackChannels');

  const { mode } = useRffFormState();
  const isReadOnly = mode === 'view';

  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  const { values } = useFormState<FormModel>();
  const workspaceId = values.slackWorkspaceId;

  const { data: channelsData } = useGetSlackChannels({
    workspaceId: workspaceId || '',
    enabled: !!workspaceId,
  });

  const channelsById = useMemo(() => {
    const map = new Map<string, SlackChannel>();
    for (const channel of channelsData?.channels || []) {
      if (channel.id) {
        map.set(channel.id, channel);
      }
    }
    return map;
  }, [channelsData]);

  const selectedChannels = channelsField.value || [];
  const hasPrivateChannel = selectedChannels.some(
    (id) => id.startsWith('G') || channelsById.get(id)?.isPrivate === true
  );
  const hasReachedLimit = selectedChannels.length >= MAX_CHANNELS;
  const remainingSlots = MAX_CHANNELS - selectedChannels.length;

  const trimmedInput = inputValue.trim();
  const isValidFormat =
    trimmedInput === '' || CHANNEL_ID_REGEX.test(trimmedInput.toUpperCase());
  const inlineError = !isValidFormat
    ? 'IDs start with C or G followed by 8 to 11 letters or digits.'
    : '';

  const handleAdd = () => {
    const id = inputValue.trim().toUpperCase();
    if (!id) return;

    if (!CHANNEL_ID_REGEX.test(id)) {
      setError(
        'Not a valid channel ID. IDs start with C or G followed by 8 to 11 letters or digits.'
      );
      return;
    }

    if (selectedChannels.includes(id)) {
      setError('This channel is already added.');
      return;
    }

    channelsField.onChange([...selectedChannels, id]);
    setInputValue('');
    setError('');
  };

  const handleRemove = (idToRemove: string) => {
    channelsField.onChange(selectedChannels.filter((id) => id !== idToRemove));
  };

  // Combine local validation error with form-level (Yup) error.
  // makeValidateSync wraps each Yup error message in an array, so unwrap
  // the first message before checking the type.
  const fieldError = Array.isArray(channelsMeta.error)
    ? channelsMeta.error[0]
    : channelsMeta.error;
  const formError =
    channelsMeta.touched && typeof fieldError === 'string'
      ? fieldError
      : undefined;
  const displayError = error || inlineError || formError;

  const hintText = hasReachedLimit
    ? `Maximum of ${MAX_CHANNELS} channels added`
    : !displayError
    ? 'Paste the ID of a public or private channel. Press Enter to add.'
    : undefined;

  return (
    <Stack>
      <Stack direction="row" gap="8px" alignItems="flex-start">
        <Input
          value={inputValue}
          onChange={(_, value) => {
            setInputValue(value);
            if (error) setError('');
          }}
          onEnter={handleAdd}
          placeholder="e.g. C04ABCD123"
          disabled={isReadOnly || hasReachedLimit}
          error={displayError}
          hint={displayError ? undefined : hintText}
          testId="slack-channel-id-input"
          inputProps={{
            spellCheck: false,
            style: {
              fontFamily: "'Space Mono', monospace",
              fontSize: '14px',
            },
          }}
          sx={{ flex: 1 }}
        />
        <Button
          variant="primary"
          onClick={handleAdd}
          disabled={
            isReadOnly || hasReachedLimit || !trimmedInput || !isValidFormat
          }
          testId="slack-channel-add-button"
        >
          Add
        </Button>
      </Stack>
      {!hasReachedLimit && remainingSlots <= 2 && (
        <Typography
          variant="P12R"
          color="textSecondary"
          sx={{ pt: '4px' }}
          component="span"
        >
          {remainingSlots} {remainingSlots === 1 ? 'channel' : 'channels'}{' '}
          remaining (max {MAX_CHANNELS})
        </Typography>
      )}
      {selectedChannels.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap="8px" sx={{ pt: '16px' }}>
          {selectedChannels.map((id) => {
            const channel = channelsById.get(id);
            const isPrivate = id.startsWith('G') || channel?.isPrivate === true;
            const displayName = channel?.name;

            return (
              <Stack
                key={id}
                direction="row"
                alignItems="center"
                gap="8px"
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'background.paper',
                  borderRadius: '4px',
                  padding: '6px 8px 6px 12px',
                  fontSize: '13px',
                }}
              >
                <Icons.Check size={14} weight="bold" />
                {isPrivate ? (
                  // Private channel: NEVER show the actual name
                  <>
                    <Typography
                      variant="C14"
                      sx={{ fontWeight: 500, color: 'text.primary' }}
                    >
                      Private channel
                    </Typography>
                    <Typography
                      variant="C12"
                      sx={{
                        fontFamily: "'Space Mono', monospace",
                        color: 'text.secondary',
                        fontSize: '12px',
                      }}
                    >
                      {id}
                    </Typography>
                  </>
                ) : displayName ? (
                  // Public channel with known name: show name + ID
                  <>
                    <Typography
                      variant="C14"
                      sx={{ fontWeight: 500, color: 'text.primary' }}
                    >
                      #{displayName}
                    </Typography>
                    <Typography
                      variant="C12"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '12px',
                      }}
                    >
                      {id}
                    </Typography>
                  </>
                ) : (
                  // Unknown channel (not in fetched list): show ID only
                  <Typography
                    variant="C12"
                    sx={{ fontFamily: "'Space Mono', monospace" }}
                  >
                    {id}
                  </Typography>
                )}
                {!isReadOnly && (
                  <IconButton
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove channel ${id}`}
                    onClick={() => handleRemove(id)}
                    testId={`slack-channel-remove-${id}`}
                    sx={{ width: 14, height: 14 }}
                  >
                    <Icons.X size={14} />
                  </IconButton>
                )}
              </Stack>
            );
          })}
        </Stack>
      )}
      {hasPrivateChannel && <PrivateChannelNoteInline />}
      <PrivateChannelNote />
    </Stack>
  );
};
